// Lists are allowed to share a name (the backend doesn't enforce uniqueness), but
// the list pickers and the `?list=NAME` URL key lists by name. Give every list a
// unique display name so name-keyed lookups always resolve to one list.
//
// The oldest list (lowest id) keeps its name; later copies become "Name (2)",
// "Name (3)", … — skipping any suffix a real list already uses. Ordering is by id,
// so every hook that loads the same lists derives the same names.

interface NamedList {
  id?: string | number | null;
  name?: string | null;
}

const numericId = (l: NamedList) => {
  const n = Number(l.id);
  return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER;
};

/**
 * Returns `lists` in the same order, with duplicate names suffixed. Lists whose
 * name is already unique are returned as-is; the array itself is returned
 * unchanged when there are no duplicates.
 */
export function withUniqueListNames<T extends NamedList>(lists: T[]): T[] {
  const counts = new Map<string, number>();
  for (const l of lists) if (l?.name) counts.set(l.name, (counts.get(l.name) ?? 0) + 1);
  if (![...counts.values()].some(c => c > 1)) return lists;

  const taken = new Set(counts.keys());
  const renamed = new Map<T, string>();
  const seen = new Set<string>();
  const byAge = [...lists].filter(l => l?.name).sort((a, b) => numericId(a) - numericId(b));
  for (const l of byAge) {
    const name = l.name!;
    if (!seen.has(name)) {
      seen.add(name);
      continue;
    }
    let n = 2;
    while (taken.has(`${name} (${n})`)) n++;
    const unique = `${name} (${n})`;
    taken.add(unique);
    renamed.set(l, unique);
  }
  return lists.map(l => (renamed.has(l) ? { ...l, name: renamed.get(l)! } : l));
}
